/**
 * Подписант юрлица — как он попадает в акт.
 *
 * Акт использует подписанта ДВАЖДЫ и по-разному (сверено по 29 реальным актам
 * лаборатории):
 *
 *   в преамбуле  «… в лице генерального директора Стародубцевой Анны Евгеньевны, ОГРН …»
 *   над печатью  «____________ А.Е. Стародубцева»
 *
 * Первое — родительный падеж, второе — именительный с инициалами впереди.
 * Хранится одно: должность и ФИО в именительном. Родительный выводится
 * правилами, но правила эти для русских фамилий работают не всегда, поэтому
 * есть ручное переопределение — и именно оно заполнено для существующих
 * записей оборотами, набранными человеком.
 *
 * В счёте подписанта заказчика нет вовсе: счёт подписывает только исполнитель.
 */
// ---------------------------------------------------------------------------
// Склонение
// ---------------------------------------------------------------------------
/**
 * Должности в родительном падеже.
 *
 * Список закрытый: за 38 юрлицами лаборатории стоят четыре должности. Общего
 * правила для произвольной должности нет («заместитель» → «заместителя», но
 * «управляющий» → «управляющего»), поэтому неизвестная возвращается как есть,
 * а не коверкается наугад — лучше видимая шероховатость, чем тихая ошибка
 * в документе.
 */
const POSITION_GENITIVE = {
    'генеральный директор': 'генерального директора',
    'заместитель генерального директора': 'заместителя генерального директора',
    'исполнительный директор': 'исполнительного директора',
    'финансовый директор': 'финансового директора',
    'управляющий': 'управляющего',
    'директор': 'директора',
};
export function positionGenitive(position) {
    const key = position.trim().toLowerCase();
    return POSITION_GENITIVE[key] ?? position.trim();
}
/**
 * Женский род определяется по ОТЧЕСТВУ, а не по фамилии или имени.
 *
 * Отчество — единственная надёжная примета: фамилия может не склоняться
 * («Приходько», «Лю»), а имя быть незнакомым («Кюнней», «Дыжид»).
 */
function isFeminine(patronymic) {
    return /(овна|евна|ична|инична|кызы)$/i.test(patronymic.trim());
}
/**
 * Родительный падеж фамилии.
 *
 * Покрывает то, что встречается в данных лаборатории. Несклоняемые формы
 * (на -о, -ко, -ых, -их, короткие иностранные вроде «Лю») возвращаются без
 * изменений — это правило русского языка, а не пропуск.
 */
export function surnameGenitive(surname, patronymic = '') {
    const name = surname.trim();
    if (!name)
        return '';
    const feminine = isFeminine(patronymic);
    // Несклоняемые: Приходько, Николко, Черных, Долгих, Лю, Дурново.
    if (/(ко|ых|их|о|е|у|ю|и|ы)$/i.test(name))
        return name;
    if (feminine) {
        if (/(ская|цкая|ая)$/i.test(name))
            return name.replace(/ая$/i, 'ой');
        if (/(ова|ева|ёва|ина|ына)$/i.test(name))
            return `${name.slice(0, -1)}ой`;
        // Женская фамилия на согласный не склоняется: Ткачук, Спасич, Шевчук.
        return name;
    }
    if (/(ский|цкий|ый|ий)$/i.test(name))
        return name.replace(/(ый|ий)$/i, 'ого');
    if (/(ов|ев|ёв|ин|ын)$/i.test(name))
        return `${name}а`;
    if (/(ь|й)$/i.test(name))
        return `${name.slice(0, -1)}я`;
    if (/[бвгджзклмнпрстфхцчшщ]$/i.test(name))
        return `${name}а`;
    if (/а$/i.test(name))
        return `${name.slice(0, -1)}ы`;
    return name;
}
/**
 * Родительный падеж имени: Павел → Павла, Сергей → Сергея, Анна → Анны.
 *
 * Род обязателен: женское имя, не оканчивающееся на -а/-я, по-русски НЕ
 * склоняется. Без этого якутское «Кюнней» превращалось в «Кюннея» — по
 * мужскому образцу «Сергей → Сергея».
 */
export function givenNameGenitive(given, feminine = false) {
    const name = given.trim();
    if (!name)
        return '';
    if (/(ия|ья)$/i.test(name))
        return `${name.slice(0, -1)}и`;
    if (/я$/i.test(name))
        return `${name.slice(0, -1)}и`;
    if (/а$/i.test(name)) {
        // После шипящих и «к», «г», «х» пишется «и»: Ольга → Ольги, Инга → Инги.
        return /[жчшщкгх]а$/i.test(name) ? `${name.slice(0, -1)}и` : `${name.slice(0, -1)}ы`;
    }
    // Дальше только мужские образцы. Женское имя на согласный неизменяемо:
    // Кюнней, Айгуль, Кармен, Ирэн.
    if (feminine)
        return name;
    if (/(ий|ей|ай|ой)$/i.test(name))
        return `${name.slice(0, -1)}я`;
    if (/ь$/i.test(name))
        return `${name.slice(0, -1)}я`;
    // Беглая гласная: Павел → Павла. Единственный частотный случай.
    if (/ел$/i.test(name))
        return `${name.slice(0, -2)}ла`;
    if (/[бвгджзйклмнпрстфхцчшщ]$/i.test(name))
        return `${name}а`;
    return name;
}
/** Родительный падеж отчества: Евгеньевна → Евгеньевны, Олегович → Олеговича. */
export function patronymicGenitive(patronymic) {
    const name = patronymic.trim();
    if (!name)
        return '';
    if (/(овна|евна|ична|инична|на)$/i.test(name))
        return `${name.slice(0, -1)}ы`;
    if (/(ович|евич|ич)$/i.test(name))
        return `${name}а`;
    return name;
}
/** Разбор «Стародубцева Анна Евгеньевна» на части. */
export function parseName(fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    return {
        surname: parts[0] ?? '',
        given: parts[1] ?? '',
        patronymic: parts.slice(2).join(' '),
    };
}
/** ФИО в родительном падеже: «Стародубцевой Анны Евгеньевны». */
export function fullNameGenitive(fullName) {
    const { surname, given, patronymic } = parseName(fullName);
    return [
        surnameGenitive(surname, patronymic),
        givenNameGenitive(given, isFeminine(patronymic)),
        patronymicGenitive(patronymic),
    ]
        .filter(Boolean)
        .join(' ');
}
/**
 * Оборот для преамбулы акта — БЕЗ слов «в лице»: их подставляет шаблон.
 *
 * Ручное переопределение имеет приоритет над правилами: в базу перенесены
 * обороты из существующих актов, набранные человеком, и переизобретать их
 * заново незачем. Пустая строка означает, что подписанта нет (ИП действует
 * лично) — тогда оборот в документе не появляется.
 */
export function signerInPersonOf(legal) {
    const manual = legal.signerGenitive.trim();
    if (manual)
        return manual;
    const position = positionGenitive(legal.signerPosition);
    const name = fullNameGenitive(legal.signerName);
    return [position, name].filter(Boolean).join(' ');
}
/**
 * Подпись над печатью: «А.Е. Стародубцева» — инициалы ВПЕРЕДИ фамилии.
 *
 * Именно такой порядок в актах лаборатории. Выводится из именительного ФИО
 * и переопределению не подлежит: ошибиться тут правилами невозможно.
 */
export function signerSignatureOf(legal) {
    const { surname, given, patronymic } = parseName(legal.signerName);
    if (!surname)
        return '';
    const initials = [given, patronymic]
        .filter(Boolean)
        .map((part) => `${part.trim()[0]?.toUpperCase()}.`)
        .join('');
    return initials ? `${initials} ${surname}` : surname;
}
//# sourceMappingURL=signer.js.map